import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { securityApi } from '../services/securityApi';
import type { IntelSource, IntelSourceUpdateBody } from '../types/security';

interface SourceEditModalProps {
  source: IntelSource;
  onClose: () => void;
  onSaved: () => void;
}

const SCHEDULE_INTERVALS = ['hourly', 'daily', 'weekly'] as const;

export function SourceEditModal({
  source,
  onClose,
  onSaved,
}: SourceEditModalProps): React.JSX.Element {
  const { authFetch } = useAuth();
  const [scheduleInterval, setScheduleInterval] = useState(
    source.schedule_interval ?? 'daily',
  );
  const [isActive, setIsActive] = useState(source.is_active);
  const [rateLimit, setRateLimit] = useState<string>(
    source.config?.rate_limit_seconds != null
      ? String(source.config.rate_limit_seconds)
      : '',
  );
  const [userAgent, setUserAgent] = useState<string>(
    typeof source.config?.user_agent === 'string'
      ? (source.config.user_agent as string)
      : '',
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    const body: IntelSourceUpdateBody = {
      schedule_interval: scheduleInterval as 'hourly' | 'daily' | 'weekly',
      is_active: isActive,
    };
    const config: Record<string, unknown> = {};
    if (rateLimit !== '') {
      const num = Number(rateLimit);
      if (Number.isFinite(num) && num > 0) {
        config.rate_limit_seconds = num;
      } else {
        setError('rate_limit_seconds must be a positive number');
        setSubmitting(false);
        return;
      }
    }
    if (userAgent.trim()) {
      config.user_agent = userAgent.trim();
    }
    if (Object.keys(config).length) body.config = config;

    try {
      await securityApi.updateIntelSource(authFetch, source.id, body);
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="source-edit-title"
        tabIndex={-1}
        style={{
          background: 'var(--bg-primary, white)',
          padding: '1.5rem',
          borderRadius: '0.5rem',
          maxWidth: '480px',
          width: '100%',
        }}
      >
        <h3 id="source-edit-title" style={{ marginTop: 0 }}>
          Edit source: {source.name}
        </h3>
        <form onSubmit={onSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>Schedule interval</span>
              <select
                value={scheduleInterval ?? ''}
                onChange={(e) => setScheduleInterval(e.target.value)}
                aria-label="Schedule interval"
              >
                {SCHEDULE_INTERVALS.map((i) => (
                  <option key={i} value={i}>
                    {i}
                  </option>
                ))}
              </select>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              <span>Active</span>
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>Rate limit seconds (config.rate_limit_seconds)</span>
              <input
                type="text"
                inputMode="decimal"
                value={rateLimit}
                onChange={(e) => setRateLimit(e.target.value)}
                aria-label="Rate limit seconds"
              />
            </label>
            <label style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              <span>User agent (config.user_agent)</span>
              <input
                type="text"
                value={userAgent}
                onChange={(e) => setUserAgent(e.target.value)}
                aria-label="User agent"
              />
            </label>
          </div>
          {error && (
            <div
              role="alert"
              style={{
                marginTop: '0.75rem',
                color: 'var(--severity-critical-text, #991b1b)',
              }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '0.5rem',
              marginTop: '1rem',
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={submitting}
            >
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
